import PageLoading from '@/components/PageLoading';
import AdminLayout from '@/layout/AdminLayout';
import { useAuthStore } from '@/store/auth';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import AuthGuardFallback from './AuthGuardFallback';

/**
 * 登录守卫。
 *
 * 凭据在 HttpOnly Cookie 里、JS 读不到，所以判据不是「本地有没有令牌」，而是「服务端认不认」——
 * 进应用先问一次 iam 的当前会话（`store/auth.ts` 的 bootstrap），它同时把「能进哪些页、是不是超管」
 * 一起带回来。代价是首屏多一次网络往返，加载态必须做好，否则会看到登录页闪一下。
 *
 * 问的结果分三档：认 → 渲染布局；401 → 登录页；**不是 401 的失败**（断网、5xx）→
 * `AuthGuardFallback` 给一个可重试的出口，网络抖一下就把人踢出去比多等一会儿糟得多。
 */
export default function AuthGuard() {
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    if (status === 'idle') void bootstrap();
  }, [status, bootstrap]);

  const here = encodeURIComponent(`${location.pathname}${location.search}`);

  if (status === 'idle' || status === 'loading') return <PageLoading />;

  if (status === 'error') {
    return <AuthGuardFallback onRetry={() => void bootstrap()} />;
  }

  if (status === 'anon') {
    return <Navigate to={`/login?redirect=${here}`} replace />;
  }

  return <AdminLayout />;
}
